import React, { useState } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  CircularProgress,
} from '@mui/material';
import ContractsDashboard from './components/ContractsDashboard';
import InvoiceManagement from './components/InvoiceManagement';
import FinancialModelDetail from './components/FinancialModelDetail';
import PpaCard from './components/PpaCard';
import TermSheet from './components/TermSheet';
import PpaStack from './components/PpaStack';
import RevenueMix from './components/RevenueMix';
import ErrorBoundary from './components/ErrorBoundary';
import PpaReconciliation from './components/PpaReconciliation';
import SmartestInvoices from './components/SmartestInvoices';
import BatteryContracts from './components/BatteryContracts';
import VarianceWaterfall from './components/VarianceWaterfall';
import FacilityCovenants from './components/FacilityCovenants';
import PpaFairValue from './components/PpaFairValue';
import Benchmarks from './components/Benchmarks';
import TradingPnL from './components/TradingPnL';
import CommercialOverview from './components/CommercialOverview';
import ClearingPrice from './components/ClearingPrice';
import FppSettlement from './components/FppSettlement';
import PortfoliosDashboard from './components/PortfoliosDashboard';
import PortfolioOverview from './components/PortfolioOverview';
import TechnicalSubview from './components/TechnicalSubviews';
import ScadaSubview from './components/ScadaSubviews';
import MetisOwnerPortal from './clients/metis/OwnerPortal';
import AlertsCentre from './components/AlertsCentre';
import PortfolioPerformance from './components/PortfolioPerformance';
import PortfolioCurtailment from './components/PortfolioCurtailment';
import TechnicalAvailability from './components/TechnicalAvailability';
import CalendarView from './components/CalendarView';
import RevenueAnalysis from './components/RevenueAnalysis';
import FinancialReporting from './components/FinancialReporting';
import LoginPage from './components/LoginPage';
import SignUpPage from './components/SignUpPage';
import ProfileSetup from './components/ProfileSetup';
import SettingsPage from './components/SettingsPage';
import PortfolioAgent from './components/PortfolioAgent';
import AgentWorkforce from './components/AgentWorkforce';
import MarketHome from './components/market/MarketHome';
import MarketPrice from './components/market/MarketPrice';
import MarketDemand from './components/market/MarketDemand';
import MarketInterconnectors from './components/market/MarketInterconnectors';
import MarketFcas from './components/market/MarketFcas';
import MarketBattery from './components/market/MarketBattery';
import MarketGeneration from './components/market/MarketGeneration';
import MarketNetworkMap from './components/market/MarketNetworkMap';
import MarketTransmission from './components/market/MarketTransmission';
import DashboardSidebar from './components/DashboardSidebar';
import { ThemeModeProvider } from './theme/ThemeModeContext';
import { useAuth } from './contexts/AuthContext';
import { auth } from './firebase';

// Set up axios interceptor to attach Firebase token to all requests
axios.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    try {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (e) {
      // Token fetch failed, continue without auth header
    }
  }
  return config;
});

const SIDEBAR_EXPANDED = 260;
const SIDEBAR_COLLAPSED = 72;

// Shared spinner shown while auth/profile is loading. Prevents a flash of
// /setup-profile on login (user is set before profile fetch completes).
const AuthLoadingSpinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <CircularProgress sx={{ color: '#D98A2B' }} />
  </Box>
);

// Protected route wrapper
const ProtectedRoute = ({ children, requireProfile = false }) => {
  const { user, loading, profileLoading, profileComplete } = useAuth();

  if (loading || (user && profileLoading)) {
    return <AuthLoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireProfile && !profileComplete) {
    return <Navigate to="/setup-profile" replace />;
  }

  return children;
};

// Public route wrapper — redirects to app if already logged in
const PublicRoute = ({ children }) => {
  const { user, loading, profileLoading, profileComplete } = useAuth();

  if (loading || (user && profileLoading)) {
    return <AuthLoadingSpinner />;
  }

  if (user) {
    if (!profileComplete) {
      return <Navigate to="/setup-profile" replace />;
    }
    return <Navigate to="/portfolios" replace />;
  }

  return children;
};

// Tiny helper that redirects /portfolios/:id/battery → /portfolios/:id/commercial
// so old bookmarks of the standalone hardcoded-VBB1 page land on the new
// fuel-aware Commercial Overview instead.
function RedirectToCommercial() {
  const { portfolioId } = useParams();
  return <Navigate to={`/portfolios/${portfolioId}/commercial`} replace />;
}

// Old benchmark routes funnel into the unified /benchmarks page with the
// matching ?view= so existing bookmarks land on the right tab.
function RedirectToBenchmarks({ view }) {
  const { portfolioId } = useParams();
  return <Navigate to={`/portfolios/${portfolioId}/benchmarks?view=${view}`} replace />;
}

function RedirectToFairValue() {
  const { portfolioId } = useParams();
  return <Navigate to={`/portfolios/${portfolioId}/ppas/fair-value`} replace />;
}

// Main app layout — full-height left menu (logo top, nav middle, account
// bottom); no top app bar. The menu collapses to an icon rail.
const AppLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;
  const location = useLocation();

  // Extract portfolio context from URL: /portfolios/:id/contracts etc.
  const pathParts = location.pathname.split('/').filter(Boolean);
  const portfolioId = pathParts[0] === 'portfolios' && pathParts[1] ? pathParts[1] : null;
  const subPage = portfolioId && pathParts[2] ? pathParts[2] : null;

  // Determine active page
  // Map URL → top-level section (sidebar grouping). Multiple sub-pages roll
  // up to one of the 6 sections so the sidebar item stays highlighted no
  // matter which tab inside the section is open. Section ids match the
  // sidebar config.
  let activePage = 'portfolios'; // list view
  if (location.pathname.startsWith('/market'))               activePage = 'market';
  else if (location.pathname.startsWith('/alerts'))          activePage = 'alerts';
  else if (portfolioId && !subPage)                          activePage = 'portfolio';
  else if (subPage === 'overview')                           activePage = 'portfolio';
  else if (subPage === 'covenants')                          activePage = 'facility';
  else if (subPage === 'contracts' || subPage === 'financial-models') activePage = 'documents';
  else if (subPage === 'calendar')                           activePage = 'calendar';
  else if (subPage === 'ppas')                               activePage = 'contracts';
  else if (subPage === 'invoices')                           activePage = 'invoices';
  else if (['commercial', 'variance', 'trading', 'clearing-price', 'fpp'].includes(subPage)) activePage = 'commercial';
  else if (subPage === 'battery') {
    // Battery sits in two sections: Commercial (default /battery) + Technical
    // (/battery/contracts, the obligations page). Use the deeper segment to
    // disambiguate.
    activePage = pathParts[3] === 'contracts' ? 'technical' : 'commercial';
  }
  else if (['performance', 'availability', 'curtailment', 'outages', 'forecast-accuracy',
            'degradation'].includes(subPage)) activePage = 'technical';
  else if (['benchmarks', 'asset-benchmarks', 'peer-benchmark'].includes(subPage))
    activePage = 'benchmarks';
  else if (subPage === 'agents')                             activePage = 'agents';
  else if (subPage === 'agent')                              activePage = 'agent';
  else if (subPage === 'revenue' || subPage === 'financial-reporting') {
    // Legacy pages — sidebar no longer surfaces them. Routes still work for
    // backwards compatibility but the section grouping rolls them under
    // Commercial Performance to avoid an undefined active state.
    activePage = 'commercial';
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <DashboardSidebar
        width={sidebarWidth}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        activePage={activePage}
        portfolioId={portfolioId}
      />
      <Box
        component="main"
        sx={{
          px: { xs: 1.5, sm: 2, md: 3 },
          py: 2,
          minHeight: '100vh',
          boxSizing: 'border-box',
          ml: `${sidebarWidth}px`,
          width: `calc(100% - ${sidebarWidth}px)`,
          transition: 'margin-left 0.2s ease, width 0.2s ease',
        }}
      >
        {/* Top-level boundary — any uncaught render error in a page is
            confined to this content area. The menu stays alive so the user
            can navigate elsewhere instead of staring at a blank screen. */}
        <ErrorBoundary name="This page">
          {children}
        </ErrorBoundary>
      </Box>
    </Box>
  );
};

// Sends /portfolios/:portfolioId straight to /overview so users always land
// on the new Aurora dashboard. Old PortfoliosDashboard (legacy detail view)
// stays available via direct sub-routes (/performance, /curtailment, etc.).
function RedirectToOverview() {
  const { portfolioId } = useParams();
  return <Navigate to={`/portfolios/${portfolioId}/overview`} replace />;
}

function App() {
  return (
    <ThemeModeProvider>
      <Routes>
        {/* Public routes (login/signup) */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <SignUpPage />
            </PublicRoute>
          }
        />

        {/* Profile setup (authenticated but profile not complete) */}
        <Route
          path="/setup-profile"
          element={
            <ProtectedRoute>
              <ProfileSetup />
            </ProtectedRoute>
          }
        />

        {/* Protected app routes */}
        <Route
          path="/alerts"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <AlertsCentre />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <PortfoliosDashboard />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Bare /portfolios/:id redirects to /overview so legacy links land on the new dashboard. */}
        <Route
          path="/portfolios/:portfolioId"
          element={<RedirectToOverview />}
        />
        {/* Aurora-style merged Portfolio Overview (Cluster 1.1) */}
        <Route
          path="/portfolios/:portfolioId/overview"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <PortfolioOverview />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/performance"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <PortfolioPerformance />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/availability"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <TechnicalAvailability />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/curtailment"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <PortfolioCurtailment />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Benchmarks workspace — three views (asset / peer / series) in a
            single page. Legacy URLs /asset-benchmarks and /peer-benchmark
            redirect to /benchmarks?view=... so old bookmarks keep working. */}
        <Route
          path="/portfolios/:portfolioId/benchmarks"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <Benchmarks />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/asset-benchmarks"
          element={<RedirectToBenchmarks view="asset" />}
        />
        {/* /peer-benchmark used to be a separate view; folded into
            Asset benchmarks (auto-suggest peers live in the Edit dialog). */}
        <Route
          path="/portfolios/:portfolioId/peer-benchmark"
          element={<RedirectToBenchmarks view="asset" />}
        />
        {/* Technical sub-views — one component, 3 modes by URL segment.
            (peer-benchmark moved to the Benchmarks page above.) */}
        {['outages', 'forecast-accuracy', 'degradation'].map((sv) => (
          <Route
            key={sv}
            path={`/portfolios/:portfolioId/${sv}`}
            element={
              <ProtectedRoute requireProfile>
                <AppLayout>
                  <TechnicalSubview subview={sv} />
                </AppLayout>
              </ProtectedRoute>
            }
          />
        ))}
        {/* Plant SCADA sub-views — one component, panel chosen by URL segment.
            The component itself gates on the organisation having a client_*
            dataset, so a market-only org gets an empty state rather than a 404. */}
        <Route
          path="/portfolios/:portfolioId/scada/:panel"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <ScadaSubview />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/scada"
          element={<Navigate to="performance" replace />}
        />
        <Route
          path="/portfolios/:portfolioId/revenue"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <RevenueAnalysis />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/financial-reporting"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <FinancialReporting />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/ppas"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                {/* TermSheet replaces the legacy PpaCard — reads new Firestore
                    shape via /api/contracts. PpaCard import retained until we
                    finish migrating the Revenue Mix tab off the old endpoint. */}
                <TermSheet />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/ppas/stack"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                {/* RevenueMix replaces the legacy PpaStack — reads /api/contracts
                    (Firestore) and projects monthly revenue from each PPA's
                    price_schedule × MEG × buyers_share. */}
                <RevenueMix />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/ppas/reconcile"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <PpaReconciliation />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/ppas/invoices"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <SmartestInvoices />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* /battery was the standalone hardcoded-VBB1 page. It's now folded
            into the Commercial Overview, which switches to battery flavour
            automatically when the asset picker is on a BESS. We redirect
            so old bookmarks land on the right page. */}
        <Route
          path="/portfolios/:portfolioId/battery"
          element={<RedirectToCommercial />}
        />
        <Route
          path="/portfolios/:portfolioId/battery/contracts"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <BatteryContracts />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/commercial"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <CommercialOverview />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/clearing-price"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <ClearingPrice />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/fpp"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <FppSettlement />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/variance"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <VarianceWaterfall />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/covenants"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <FacilityCovenants />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/ppas/fair-value"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <PpaFairValue />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Fair Value briefly lived at /fair-value as its own sidebar item
            before moving into the PPAs & Contracts tab strip. */}
        <Route
          path="/portfolios/:portfolioId/fair-value"
          element={<RedirectToFairValue />}
        />
        <Route
          path="/portfolios/:portfolioId/trading"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <TradingPnL />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/contracts"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <ContractsDashboard />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/contracts/:contractId"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <ContractsDashboard />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/invoices"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <InvoiceManagement />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/financial-models/:modelId"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <FinancialModelDetail />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolios/:portfolioId/calendar"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <CalendarView />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* AI Agent Workforce — fleet setup & orchestration showcase (UI only) */}
        <Route
          path="/portfolios/:portfolioId/agents"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <AgentWorkforce />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Portfolio AI Assistant — tool-use chat scoped to one portfolio */}
        <Route
          path="/portfolios/:portfolioId/agent"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <PortfolioAgent />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Settings — organisation, members & teams. One component, three
            route-driven tabs (SettingsWorkspaceTabs resolves the active one
            from the pathname). */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <SettingsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/members"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <SettingsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/teams"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <SettingsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Market analytics — NEM market intelligence, sibling to Portfolios.
            /market is a landing grid of dashboard cards; each opens a page. */}
        <Route
          path="/market"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <MarketHome />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/market/price"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <MarketPrice />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/market/demand"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <MarketDemand />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/market/interconnectors"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <MarketInterconnectors />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/market/fcas"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <MarketFcas />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/market/battery"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <MarketBattery />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/market/generation"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <MarketGeneration />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/market/map"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <MarketNetworkMap />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/market/transmission"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <MarketTransmission />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Client-bespoke routes — outside AppLayout so the curated view
            owns its own chrome (Metis branding, no Lumeros sidebar). */}
        <Route
          path="/clients/metis/owner-portal"
          element={
            <ProtectedRoute requireProfile>
              <MetisOwnerPortal />
            </ProtectedRoute>
          }
        />
        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </ThemeModeProvider>
  );
}

export default App;
